/**
 * MONSTER TOWN — AUDIO SYNTHESIS PRIMITIVES. HUD/AUDIO/TOUCH CREW (src/audio/**).
 *
 * ZERO EXTERNAL ASSETS. Every sound in this game is built out of oscillators and
 * a generated noise buffer, right here.
 *
 * THE ONE DESIGN RULE IN THIS FILE: every scheduling function takes a
 * `BaseAudioContext`, not an `AudioContext`. That means the exact same code
 * paths run inside an `OfflineAudioContext`, which is how `tools/audio-check.mjs`
 * proves that no cue is silent. A sound that only exists in the live path is a
 * sound nobody ever verified.
 *
 * Kid-audio rules baked in here:
 *  - Nothing harsh. Everything is low-passed and soft-attacked; no clicks.
 *  - Nothing loud. Layer gains are small and the master bus sits well under 1.0.
 *  - Nothing long. Feedback must land inside ~600 ms or it stops feeling like a
 *    reaction to the tap.
 */

// ---------------------------------------------------------------------------
// layers — the vocabulary every cue is written in (see cues.ts, which is DATA)
// ---------------------------------------------------------------------------

export interface ToneLayer {
  readonly kind: 'tone'
  readonly wave: OscillatorType
  readonly freq: number
  /** Pitch bends to here across the layer's life. Omit for a steady note. */
  readonly toFreq?: number
  /** Seconds after the cue start. */
  readonly at?: number
  readonly dur: number
  readonly gain: number
  readonly attack?: number
  /** Detuned second oscillator, in cents. Fattens a tone for free. */
  readonly detune?: number
  /** Lowpass cutoff. Everything is soft by default. */
  readonly lowpass?: number
  readonly vibrato?: { readonly rate: number; readonly depth: number }
}

export interface NoiseLayer {
  readonly kind: 'noise'
  readonly at?: number
  readonly dur: number
  readonly gain: number
  readonly filter?: BiquadFilterType
  readonly freq?: number
  /** Filter sweeps to here — the difference between "pfft" and "whoosh". */
  readonly toFreq?: number
  readonly q?: number
  readonly attack?: number
}

export interface PluckLayer {
  readonly kind: 'pluck'
  readonly at?: number
  readonly freq: number
  readonly dur: number
  readonly gain: number
}

/** An arpeggio. The coin chime, the unlock fanfare, the order jingle. */
export interface ChimeLayer {
  readonly kind: 'chime'
  readonly at?: number
  readonly freqs: readonly number[]
  /** Seconds between notes. */
  readonly step: number
  readonly dur: number
  readonly gain: number
  readonly wave?: OscillatorType
}

/** Silly babble — the TV, a monster talking, a radio. Never words. */
export interface ChatterLayer {
  readonly kind: 'chatter'
  readonly at?: number
  readonly dur: number
  readonly gain: number
  readonly low: number
  readonly high: number
  /** Blips per second. */
  readonly rate: number
}

export type Layer = ToneLayer | NoiseLayer | PluckLayer | ChimeLayer | ChatterLayer

// ---------------------------------------------------------------------------
// shared noise buffer, cached per context (offline contexts get their own)
// ---------------------------------------------------------------------------

const noiseBuffers = new WeakMap<BaseAudioContext, AudioBuffer>()

export function noiseBuffer(ctx: BaseAudioContext): AudioBuffer {
  const cached = noiseBuffers.get(ctx)
  if (cached !== undefined) return cached
  const len = Math.floor(ctx.sampleRate * 1.2)
  const buf = ctx.createBuffer(1, len, ctx.sampleRate)
  const data = buf.getChannelData(0)
  // Deterministic noise: the same cue must render identically every time or the
  // offline non-silence gate is measuring something different from what ships.
  let seed = 0x9e3779b9
  for (let i = 0; i < len; i++) {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0
    data[i] = (seed / 0x80000000 - 1) * 0.9
  }
  noiseBuffers.set(ctx, buf)
  return buf
}

// ---------------------------------------------------------------------------
// envelope helper — soft in, soft out, never a click
// ---------------------------------------------------------------------------

const MIN_GAIN = 0.0001

function envelope(g: GainNode, t: number, dur: number, peak: number, attack: number): void {
  const a = Math.max(0.004, Math.min(attack, dur * 0.5))
  const p = Math.max(MIN_GAIN * 2, peak)
  g.gain.setValueAtTime(MIN_GAIN, t)
  g.gain.linearRampToValueAtTime(p, t + a)
  g.gain.exponentialRampToValueAtTime(MIN_GAIN, t + dur)
  g.gain.setValueAtTime(0, t + dur + 0.001)
}

// ---------------------------------------------------------------------------
// layer scheduling
// ---------------------------------------------------------------------------

function scheduleTone(ctx: BaseAudioContext, dest: AudioNode, l: ToneLayer, t0: number): void {
  const t = t0 + (l.at ?? 0)
  const g = ctx.createGain()
  envelope(g, t, l.dur, l.gain, l.attack ?? 0.01)

  let tail: AudioNode = g
  if (l.lowpass !== undefined) {
    const f = ctx.createBiquadFilter()
    f.type = 'lowpass'
    f.frequency.setValueAtTime(l.lowpass, t)
    g.connect(f)
    tail = f
  }
  tail.connect(dest)

  const voices = l.detune === undefined ? [0] : [0, l.detune]
  for (const cents of voices) {
    const osc = ctx.createOscillator()
    osc.type = l.wave
    osc.detune.setValueAtTime(cents, t)
    osc.frequency.setValueAtTime(l.freq, t)
    if (l.toFreq !== undefined && l.toFreq > 0) {
      osc.frequency.exponentialRampToValueAtTime(l.toFreq, t + l.dur)
    }
    if (l.vibrato !== undefined) {
      const lfo = ctx.createOscillator()
      lfo.type = 'sine'
      lfo.frequency.setValueAtTime(l.vibrato.rate, t)
      const depth = ctx.createGain()
      depth.gain.setValueAtTime(l.vibrato.depth, t)
      lfo.connect(depth)
      depth.connect(osc.frequency)
      lfo.start(t)
      lfo.stop(t + l.dur + 0.02)
    }
    osc.connect(g)
    osc.start(t)
    osc.stop(t + l.dur + 0.02)
  }
}

function scheduleNoise(ctx: BaseAudioContext, dest: AudioNode, l: NoiseLayer, t0: number): void {
  const t = t0 + (l.at ?? 0)
  const src = ctx.createBufferSource()
  src.buffer = noiseBuffer(ctx)
  src.loop = true

  const g = ctx.createGain()
  envelope(g, t, l.dur, l.gain, l.attack ?? 0.008)

  if (l.filter !== undefined) {
    const f = ctx.createBiquadFilter()
    f.type = l.filter
    const from = l.freq ?? 1200
    f.frequency.setValueAtTime(from, t)
    if (l.toFreq !== undefined && l.toFreq > 0) {
      f.frequency.exponentialRampToValueAtTime(l.toFreq, t + l.dur)
    }
    if (l.q !== undefined) f.Q.setValueAtTime(l.q, t)
    src.connect(f)
    f.connect(g)
  } else {
    src.connect(g)
  }
  g.connect(dest)
  src.start(t)
  src.stop(t + l.dur + 0.02)
}

/**
 * A "pluck": a short filtered noise transient plus a fast-decaying sine at the
 * pitch. Cheaper and far more predictable than a Karplus-Strong delay loop,
 * which behaves differently at offline sample rates.
 */
function schedulePluck(ctx: BaseAudioContext, dest: AudioNode, l: PluckLayer, t0: number): void {
  const t = l.at === undefined ? t0 : t0 + l.at
  scheduleNoise(
    ctx,
    dest,
    { kind: 'noise', dur: Math.min(0.06, l.dur), gain: l.gain * 0.45, filter: 'bandpass', freq: l.freq * 2, q: 2 },
    t,
  )
  scheduleTone(
    ctx,
    dest,
    { kind: 'tone', wave: 'triangle', freq: l.freq, dur: l.dur, gain: l.gain, attack: 0.005, lowpass: l.freq * 6 },
    t,
  )
  scheduleTone(
    ctx,
    dest,
    { kind: 'tone', wave: 'sine', freq: l.freq * 2, dur: l.dur * 0.5, gain: l.gain * 0.3, attack: 0.004 },
    t,
  )
}

function scheduleChime(ctx: BaseAudioContext, dest: AudioNode, l: ChimeLayer, t0: number): void {
  const t = t0 + (l.at ?? 0)
  l.freqs.forEach((f, i) => {
    scheduleTone(
      ctx,
      dest,
      {
        kind: 'tone',
        wave: l.wave ?? 'sine',
        freq: f,
        dur: l.dur,
        gain: l.gain,
        attack: 0.006,
        at: i * l.step,
        lowpass: f * 5,
      },
      t,
    )
  })
}

function scheduleChatter(
  ctx: BaseAudioContext,
  dest: AudioNode,
  l: ChatterLayer,
  t0: number,
  rng: () => number,
): void {
  const t = t0 + (l.at ?? 0)
  const step = 1 / Math.max(1, l.rate)
  const n = Math.max(1, Math.floor(l.dur / step))
  for (let i = 0; i < n; i++) {
    const f = l.low + rng() * (l.high - l.low)
    scheduleTone(
      ctx,
      dest,
      {
        kind: 'tone',
        wave: 'square',
        freq: f,
        toFreq: f * (0.8 + rng() * 0.5),
        dur: step * 0.7,
        gain: l.gain * (0.6 + rng() * 0.4),
        attack: 0.006,
        at: i * step,
        lowpass: 1400,
      },
      t,
    )
  }
}

/** Schedule one layer. `rng` keeps babble deterministic under the harness. */
export function scheduleLayer(
  ctx: BaseAudioContext,
  dest: AudioNode,
  layer: Layer,
  t0: number,
  rng: () => number,
): void {
  switch (layer.kind) {
    case 'tone':
      scheduleTone(ctx, dest, layer, t0)
      return
    case 'noise':
      scheduleNoise(ctx, dest, layer, t0)
      return
    case 'pluck':
      schedulePluck(ctx, dest, layer, t0)
      return
    case 'chime':
      scheduleChime(ctx, dest, layer, t0)
      return
    case 'chatter':
      scheduleChatter(ctx, dest, layer, t0, rng)
      return
  }
}

/** How long a stack of layers runs, so the offline renderer knows its length. */
export function layersDuration(layers: readonly Layer[]): number {
  let end = 0
  for (const l of layers) {
    const at = l.at ?? 0
    const own = l.kind === 'chime' ? l.dur + l.step * Math.max(0, l.freqs.length - 1) : l.dur
    end = Math.max(end, at + own)
  }
  return end + 0.15
}

/** Small deterministic PRNG so a cue sounds the same in the test and the game. */
export function makeRng(seed: number): () => number {
  let s = seed >>> 0 || 1
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0
    return s / 0x100000000
  }
}
